import type {
  CurrentView,
  TPointFlowRate,
  TValueGroupLabel,
} from 'App/Map/PanelDetails/OverviewCharts/SectionTable/types'
import { cropDevelopmentColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/cropDevelopment'
import { diseasesColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/diseases'
import { environmentColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/environment'
import { getPestsColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/pests'
import { spraysColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/sprays'
import { waterManagementColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/by-view/waterManagement'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { EllipsisTooltip } from './EllipsisTooltip'
import { groupedKeys } from './OverviewCharts'
import type { emitterList } from './valueGroupEnums'

type TIrrigationActivity =
  | 'irrigationActivity_todaySoFarSum'
  | 'irrigationActivity_previous7DaysSum'
  | 'irrigationActivity_previous30DaysSum'
  | 'irrigationActivity_sinceJan1Sum'
  | 'irrigationActivity_sinceApr1Sum'
  | 'irrigationActivity_sinceJul1Sum'
  | 'irrigationActivity_sinceOct1Sum'

type TInsectTrapCatches =
  | 'insectTrapCatches_todaySum'
  | 'insectTrapCatches_previous2DaysSum'
  | 'insectTrapCatches_previous3DaysSum'
  | 'insectTrapCatches_previous7DaysSum'
  | 'insectTrapCatches_previous14DaysSum'
  | 'insectTrapCatches_previous30DaysSum'
  | 'insectTrapCatches_previous60DaysSum'
  | 'insectTrapCatches_sinceBiofixSum'
  | 'insectTrapCatches_sinceJan1Sum'
  | 'insectTrapCatches_sinceJul1Sum'

type TInsectDegreeDays =
  | 'insectDegreeDays_sinceBiofixSumMinMedianMax'
  | 'insectDegreeDays7DaysFromNow_sinceBiofixSumMinMedianMax'
  | 'insectDegreeDays14DaysFromNow_sinceBiofixSumMinMedianMax'

export type TInsectTrapCatchesKey = `${TInsectTrapCatches}_insectId${number}`

export type TInsectDegreeDaysKey = `${TInsectDegreeDays}_insectId${number}_degreeDaysC`

export type CurrentValuesDataInfo = {
  'pointName'?: string
  'blockName'?: string
  'type'?: string
  'crop'?: string
  'biofix'?: string
  'timezone': string
  'insectId'?: string
  'currentDD'?: {
    low: number | null
    high: number | null
  }
  '7dayDD'?: {
    low: number | null
    high: number | null
  }
  '14dayDD'?: {
    low: number | null
    high: number | null
  }
  'windSpeed_mps'?: number
  'windDirection_degrees'?: number
  'fav_depths'?: string[]
  'almondHullSplit_date'?: string
  'temperatureInversion_aboveVsIn_c'?: number
  'device'?: string
  [key: TInsectTrapCatchesKey]: number
  [key: TInsectDegreeDaysKey]: number
} & {
  [emitter in (typeof emitterList)[number] as `irrigationIsOn_${emitter}`]?: string
} & {
  [emitter in TIrrigationActivity as `${emitter}_${(typeof emitterList)[number]}_ms`]?: number
}

export type OverviewColumnsParams = {
  view: string | null
  selectedValueGroupsArray: TValueGroupLabel[]
  currentView: CurrentView | null
  insectIds: number[] | null
  pointsFlowRates: TPointFlowRate[] | null
  timestamp: string | null
  isSouthernHemisphere: boolean
  propertyId: number
}

const columnsByView: Partial<Record<string, Record<string, TValueGroupColumns>>> = {
  environment: environmentColumns,
  waterManagement: waterManagementColumns,
  sprays: spraysColumns,
  cropDevelopment: cropDevelopmentColumns,
  diseases: diseasesColumns,
}

const getValueGroupColumns = (view: string, valueGroup: string, insectIds: number[] | null) =>
  view === 'pests' ? getPestsColumns(valueGroup, insectIds) : columnsByView[view]?.[valueGroup]

const nameColumn = (dataIndex: 'blockName' | 'pointName', view: string | null): TColumn => ({
  title: (
    <EllipsisTooltip
      fullText={
        dataIndex === 'blockName'
          ? translate.phrases.banyanApp('Blocks')
          : translate.phrases.banyanApp('Stations')
      }
    />
  ),
  dataIndex,
  key: `${dataIndex}_${view}`,
  width: 250,
  fixed: 'left',
  render: (value: string) => <EllipsisTooltip fullText={value} />,
})

const lastFetchedColumn = (timestamp: string): TColumn => ({
  title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Last Fetched')} />,
  dataIndex: 'lastFetched',
  key: 'lastFetched',
  fixed: 'right',
  width: 200,
  render: () => {
    const momentTime = moment.tz(timestamp, moment.tz.guess())

    return `${translate.dates.format(momentTime, 'ddd, MMM D')}, ${translate.dates.format(momentTime, 'h:mm a')}`
  },
})

export const OverviewColumns = ({
  view,
  selectedValueGroupsArray,
  currentView,
  insectIds,
  pointsFlowRates,
  timestamp,
  isSouthernHemisphere,
  propertyId,
}: OverviewColumnsParams): TColumn[] => {
  const { selectedValueGroups } = selectedValueGroupsStore.getState()

  const isIrrigationOn = groupedKeys.irrigation.some((key) =>
    Boolean(selectedValueGroups[key as TValueGroup]),
  )

  // The irrigation value groups share one table. If one of them is on, none of them is off.
  const isOff = (valueGroup: string) =>
    selectedValueGroups[valueGroup as TValueGroup] === false &&
    !(isIrrigationOn && groupedKeys.irrigation.includes(valueGroup))

  const valueGroups = view
    ? selectedValueGroupsArray
        .filter(({ value }) => !isOff(value))
        .flatMap(({ value }) => getValueGroupColumns(view, value, insectIds) ?? [])
    : []

  const hasLeadingColumn = (dataIndex: 'blockName' | 'pointName') =>
    valueGroups.some(({ leadingColumn }) => leadingColumn === dataIndex)

  const showBlocks = hasLeadingColumn('blockName') && (view !== 'pests' || currentView?.pest === 'BLOCK')
  const context = { currentView, isSouthernHemisphere, pointsFlowRates, propertyId }

  return [
    ...(showBlocks ? [nameColumn('blockName', view)] : []),
    ...(hasLeadingColumn('pointName') ? [nameColumn('pointName', view)] : []),
    ...valueGroups.flatMap(({ getColumns }) => getColumns(context)),
    ...(timestamp ? [lastFetchedColumn(timestamp)] : []),
  ]
}
